import React, { useState, useCallback, useRef } from 'react';
import { useSearchParams as useUrlSearchParams } from 'react-router-dom';
import SearchInput, { SearchInputHandle } from '../../../components/SearchInput';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Switch,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import CampaignIcon from '@mui/icons-material/Campaign';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { rootStore } from '../../../stores/RootStore';
import i18next from '../../../lang';
import { adminStyles } from '../../../theme/adminStyles';
import MobileCardView, { MobileCardField } from '../../../components/MobileCardView';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import InfiniteScrollFooter from '../../../components/InfiniteScrollFooter';

interface AnnouncementListItem {
  id: number;
  userId: string;
  title: string;
  content: string;
  isImportant: number;
  isHidden: boolean;
  publishStart: string | null;
  publishEnd: string | null;
  scheduleStatus: string;
  createdAt: string;
  updatedAt: string;
}

const AnnouncementList = observer(() => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authStore, viewStatusStore } = rootStore;

  // URL クエリと同期 (戻るボタンで検索状態を復元するため)
  const [urlParams, setUrlParams] = useUrlSearchParams();
  const [searchTerm, setSearchTerm] = useState(urlParams.get('keyword') || '');
  const [importanceFilter, setImportanceFilter] = useState<string>(urlParams.get('importance') || 'all');
  const [includeHidden, setIncludeHidden] = useState(urlParams.get('includeHidden') !== 'false');
  const [scheduleFilter, setScheduleFilter] = useState<string>(urlParams.get('scheduleFilter') || '');

  const syncToUrl = useCallback((overrides?: {
    keyword?: string;
    importance?: string;
    includeHidden?: boolean;
    scheduleFilter?: string;
  }) => {
    const next = new URLSearchParams();
    const keyword = overrides?.keyword !== undefined ? overrides.keyword : searchTerm;
    const importance = overrides?.importance !== undefined ? overrides.importance : importanceFilter;
    const incHidden = overrides?.includeHidden !== undefined ? overrides.includeHidden : includeHidden;
    const sched = overrides?.scheduleFilter !== undefined ? overrides.scheduleFilter : scheduleFilter;

    if (keyword) next.set('keyword', keyword);
    if (importance && importance !== 'all') next.set('importance', importance);
    if (!incHidden) next.set('includeHidden', 'false');
    if (sched) next.set('scheduleFilter', sched);

    setUrlParams(next, { replace: true });
  }, [searchTerm, importanceFilter, includeHidden, scheduleFilter, setUrlParams]);

  const user = authStore.user;
  const isAdmin = user?.level === 'ADMIN';

  const fetchAnnouncements = useCallback(async (page: number, pageSize: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      rowsPerPage: pageSize.toString(),
      keyword: searchTerm,
      importance: importanceFilter,
      includeHidden: isAdmin && includeHidden ? 'true' : 'false'
    });
    if (scheduleFilter) {
      params.set('scheduleFilter', scheduleFilter);
    }

    const response = await axios.get(`/api/v1/admin/announcements?${params}`);
    return { items: response.data.announcements, totalCount: response.data.totalCount };
  }, [searchTerm, importanceFilter, isAdmin, includeHidden, scheduleFilter]);

  const { items: announcements, totalCount, isLoading, hasMore, sentinelRef, reload } = useInfiniteScroll<AnnouncementListItem>({
    fetchFn: fetchAnnouncements,
    pageSize: 20,
    resetDeps: [searchTerm, importanceFilter, scheduleFilter, includeHidden],
  });

  const searchInputRef = useRef<SearchInputHandle>(null);

  const handleSearch = useCallback((keyword: string) => {
    setSearchTerm(keyword);
    syncToUrl({ keyword });
  }, [syncToUrl]);

  const handleView = (announcement: AnnouncementListItem) => {
    navigate(`/admin/announcements/${announcement.id}`);
  };

  const handleEdit = (announcement: AnnouncementListItem) => {
    navigate(`/admin/announcements/edit/${announcement.id}`);
  };

  const handleDelete = async (announcement: AnnouncementListItem) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });
      await axios.delete(`/api/v1/admin/announcements/${announcement.id}`);
      reload();
    } catch (error: any) {
      console.error('Failed to delete announcement:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleToggleHidden = async (announcement: AnnouncementListItem) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });
      await axios.patch(`/api/v1/admin/announcements/${announcement.id}/toggle-hidden`);
      reload();
    } catch (error: any) {
      console.error('Failed to toggle hidden:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatScheduleDate = (dateString: string | null) => {
    if (!dateString) return '';
    // DBの値はJST（ローカル時刻）で保存されているので、UTC扱い（+'Z'）にしない
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const renderPublishPeriod = (row: AnnouncementListItem) => {
    if (row.scheduleStatus === 'PUBLISHED') return null;
    const start = formatScheduleDate(row.publishStart);
    const end = formatScheduleDate(row.publishEnd);
    if (!start && !end) return null;
    return (
      <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
        {start || '—'} ～ {end || '—'}
      </Typography>
    );
  };

  const mobileCardFields: MobileCardField<AnnouncementListItem>[] = [
    {
      key: 'title', label: i18next.t('common.title'), primary: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', opacity: row.isHidden ? 0.5 : 1 }}>
          {row.isImportant ?
            <NotificationImportantIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> :
            <CampaignIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          }
          <Typography variant="subtitle1" sx={{
            fontWeight: row.isImportant ? 600 : 400,
            color: row.isImportant ? '#dc3545' : '#212529',
          }}>
            {row.title}
          </Typography>
          {row.isHidden && (
            <Chip label={i18next.t('invoice.hidden')} size="small" sx={{ ml: 1, fontSize: '0.7rem' }} color="default" />
          )}
        </Box>
      )
    },
    {
      key: 'createdAtAndActions', label: '', hideLabel: true,
      render: (row: AnnouncementListItem) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {i18next.t('common.created_at')} {formatDate(row.createdAt)}
            </Typography>
            {isAdmin && renderPublishPeriod(row) && (
              <Box sx={{ mt: 0.3 }}>{renderPublishPeriod(row)}</Box>
            )}
          </Box>
          {isAdmin && (
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleHidden(row); }}
                color={row.isHidden ? 'default' : 'primary'}>
                {row.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* 検索・フィルター */}
      <Paper sx={adminStyles.filterPaper}>
        <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}>
            <InputLabel>{i18next.t('announcement.importance')}</InputLabel>
            <Select
              value={importanceFilter}
              label={i18next.t('announcement.importance')}
              onChange={(e) => {
                const v = e.target.value;
                setImportanceFilter(v);
                syncToUrl({ importance: v });
              }}
            >
              <MenuItem value="all">{i18next.t('common.all')}</MenuItem>
              <MenuItem value="important">{i18next.t('common.important')}</MenuItem>
              <MenuItem value="normal">{i18next.t('common.general')}</MenuItem>
            </Select>
          </FormControl>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}>
              <InputLabel>{i18next.t('announcement.schedule_status')}</InputLabel>
              <Select
                value={scheduleFilter}
                label={i18next.t('announcement.schedule_status')}
                onChange={(e) => {
                  const v = e.target.value;
                  setScheduleFilter(v);
                  syncToUrl({ scheduleFilter: v });
                }}
              >
                <MenuItem value="">{i18next.t('common.all')}</MenuItem>
                <MenuItem value="PUBLISHED">{i18next.t('announcement.status_published')}</MenuItem>
                <MenuItem value="SCHEDULED">{i18next.t('announcement.status_scheduled')}</MenuItem>
                <MenuItem value="ACTIVE">{i18next.t('announcement.status_active')}</MenuItem>
                <MenuItem value="EXPIRED">{i18next.t('announcement.status_expired')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <SearchInput
            ref={searchInputRef}
            initialValue={searchTerm}
            onCommit={handleSearch}
            variant="outlined"
            size="small"
            placeholder={i18next.t('announcement.search_by_title')}
            sx={{ flex: 1, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
          />
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={includeHidden}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setIncludeHidden(v);
                    syncToUrl({ includeHidden: v });
                  }}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {i18next.t('invoice.include_hidden')}
                </Typography>
              }
            />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => handleSearch(searchInputRef.current?.getValue() || '')}
            sx={{ ...adminStyles.searchButton, flexShrink: 0, fontSize: '0.8rem', height: 32, ...(isMobile && { minWidth: 32, px: 0.5 }) }}
          >
            {isMobile ? <SearchIcon sx={{ fontSize: '1.1rem' }} /> : i18next.t('common.search')}
          </Button>
          {isAdmin && (
            <>
              {!isPhone && <Box sx={{ flexGrow: 1 }} />}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => navigate('/admin/announcements/add')}
                sx={{ ...adminStyles.actionButton, flexShrink: 0, fontSize: '0.8rem', height: 32 }}
              >
                {i18next.t('common.add')}
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* 공지사항 목록 */}
      <Paper sx={adminStyles.tablePaper}>
        {isMobile ? (
          <MobileCardView
            data={announcements}
            fields={mobileCardFields}
            onRowClick={(row) => handleView(row)}
            keyExtractor={(row) => row.id}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={adminStyles.tableHeaderRow}>
                  <TableCell sx={adminStyles.tableHeaderCell}>
                    {i18next.t('common.title')}
                  </TableCell>
                  {isAdmin && (
                    <TableCell sx={{ ...adminStyles.tableHeaderCell as object, width: '180px' }}>
                      {i18next.t('announcement.publish_period')}
                    </TableCell>
                  )}
                  <TableCell sx={{ ...adminStyles.tableHeaderCell as object, width: '120px' }}>
                    {i18next.t('common.created_at')}
                  </TableCell>
                  {isAdmin && (
                    <TableCell sx={{ ...adminStyles.tableHeaderCell as object, width: '150px' }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {announcements.map((announcement) => (
                  <TableRow
                    key={announcement.id}
                    sx={{
                      '&:hover': { bgcolor: '#f8f9fa' },
                      cursor: 'pointer',
                      opacity: announcement.isHidden ? 0.5 : 1,
                      '& .MuiTableCell-root': { py: '8px' }
                    }}
                    onClick={() => handleView(announcement)}
                  >
                    <TableCell>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        {announcement.isImportant ?
                            <NotificationImportantIcon style={{marginRight: 10}}/>
                            :
                            <CampaignIcon style={{marginRight: 10}} />
                        }
                        <Typography
                            variant="body2"
                            sx={{
                              fontWeight: announcement.isImportant ? 600 : 400,
                              color: announcement.isImportant ? '#dc3545' : '#212529',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                        >
                          {announcement.title}
                        </Typography>
                        {announcement.isHidden && (
                          <Chip
                            label={i18next.t('invoice.hidden')}
                            size="small"
                            sx={{ ml: 1, fontSize: '0.7rem' }}
                            color="default"
                          />
                        )}
                        {announcement.scheduleStatus === 'SCHEDULED' && (
                          <Chip label={i18next.t('announcement.status_scheduled')} size="small" color="info" sx={{ ml: 1, fontSize: '0.7rem' }} />
                        )}
                        {announcement.scheduleStatus === 'ACTIVE' && (
                          <Chip label={i18next.t('announcement.status_active')} size="small" color="success" sx={{ ml: 1, fontSize: '0.7rem' }} />
                        )}
                        {announcement.scheduleStatus === 'EXPIRED' && (
                          <Chip label={i18next.t('announcement.status_expired')} size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {renderPublishPeriod(announcement) || (
                          <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.7rem' }}>
                            {i18next.t('announcement.immediate_publish')}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        {formatDate(announcement.createdAt)}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={announcement.isHidden ? i18next.t('invoice.visible') : i18next.t('invoice.hidden')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleHidden(announcement);
                              }}
                              color={announcement.isHidden ? 'default' : 'primary'}
                            >
                              {announcement.isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(announcement);
                            }}
                            title={i18next.t('common.edit')}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(announcement);
                            }}
                            title={i18next.t('common.delete')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <InfiniteScrollFooter
          loadedCount={announcements.length}
          totalCount={totalCount}
          isLoading={isLoading}
          hasMore={hasMore}
          sentinelRef={sentinelRef}
        />
      </Paper>
    </Container>
  );
});

export default AnnouncementList;
